import { Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useContext, useState } from "react";
import { ObjectSchema, object } from "yup";
import { requiredString } from "../../../../data/yupConstants";
import {
  RightsHolderBandViewModel,
  SubmitBandViewModel,
} from "../../../../generated/data-contracts";
import { ErrorUtils, api } from "../../../../utils";
import { AvisSection } from "../../../shared/components/form";
import { AvisConfirmModalShort } from "../../../shared/components/modal";
import { AvisContext } from "../../../shared/context/AvisContext";
import { SubmitBandForm } from "./SubmitBandForm";

interface ISubmitBandProps {
  rhBands?: RightsHolderBandViewModel[];
  onSubmit: () => void;
  loading: boolean;
}
export function SubmitBand(props: Readonly<ISubmitBandProps>) {
  const { rhBands, onSubmit, loading } = props;
  const { ctxRightsHolder, res } = useContext(AvisContext);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const checkDuplicates = (name: string): boolean => {
    if (!rhBands) return true;

    let result = true;

    for (const band of rhBands) {
      if (
        band.name?.trim().toLocaleLowerCase() ===
        name.trim().toLocaleLowerCase()
      ) {
        result = false;
        break;
      }
    }

    return result;
  };

  const validationSchema: ObjectSchema<SubmitBandViewModel> = object({
    name: requiredString(res)
      .min(2, res("authors_BandName_MinLengthMessage", "2"))
      .max(100, res("authors_BandName_MaxLengthMessage", "100"))
      .test(
        "check-duplicates",
        res("authors_BandName_validationDuplicateNames"),
        checkDuplicates,
      ),
  });

  const handleSubmit = (
    values: SubmitBandViewModel,
    formikHelpers: FormikHelpers<SubmitBandViewModel>,
  ) => {
    if (!ctxRightsHolder?.rightsHolderId) return;

    setIsSubmitting(true);
    api
      .rightsHoldersPostRightsHoldersBand(
        ctxRightsHolder.rightsHolderId,
        values,
      )
      .then(() => {
        ErrorUtils.showSuccessfulMessage(
          res("message_Success"),
          res("authors_BandName_SuccessfullyAddedGroup"),
        );
        formikHelpers.resetForm();
        onSubmit();
      })
      .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
      .finally(() => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      });
  };

  const renderForm = useCallback(
    (formikProps: FormikProps<SubmitBandViewModel>) => {
      return (
        <>
          <AvisConfirmModalShort
            isOpen={isModalOpen}
            content={res("authors_BandName_ConfirmAddNewGroup")}
            confirm={formikProps.submitForm}
            cancel={() => setIsModalOpen(false)}
            title={res("title_Confirm")}
            disableControls={isSubmitting}
          />
          <SubmitBandForm
            bag={formikProps}
            loading={isSubmitting || loading}
            showModal={() => setIsModalOpen(true)}
          />
        </>
      );
    },
    [isModalOpen, isSubmitting, res, loading],
  );

  return (
    <AvisSection title={res("authors_BandName_AddNewGroupTitle")}>
      <Formik<SubmitBandViewModel>
        initialValues={{
          name: "",
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {renderForm}
      </Formik>
    </AvisSection>
  );
}
